// Los cuatro estados de una lectura, con el mismo lenguaje en toda la pantalla. Vacío-porque-carga,
// vacío-porque-no-hay, caída y denegación se leen distinto; solo la caída ofrece reintentar.
import type { ReactNode } from 'react';
import { EmptyState, ErrorState, Loading } from '@/kit';
import { userMessageOf } from '@/api/errors';
import type { ModeliaReadState } from '../types';

export interface ModeliaReadStateBlockProps {
    state: ModeliaReadState;
    loadingText: string;
    emptyTitle: string;
    emptyDescription: ReactNode;
    errorTitle: string;
    /** El motivo del fallo, tal como se le puede enseñar a un operador. */
    error: unknown;
    onRetry: () => void;
    forbiddenDescription: string;
    className?: string;
}

/** La denegación, con el mismo título en toda la pantalla: tres formas de decirlo se leen como tres. */
export function ModeliaForbiddenBlock({
    description,
    className,
}: {
    description: string;
    className?: string;
}) {
    return (
        <ErrorState
            title="No tienes acceso a esta información"
            description={description}
            className={className}
        />
    );
}

/** `null` cuando el estado es `ready`: ahí manda el contenido del tab, no este bloque. */
export function ModeliaReadStateBlock({
    state,
    loadingText,
    emptyTitle,
    emptyDescription,
    errorTitle,
    error,
    onRetry,
    forbiddenDescription,
    className,
}: ModeliaReadStateBlockProps) {
    if (state === 'ready') return null;
    if (state === 'loading') return <Loading text={loadingText} className={className} />;
    if (state === 'forbidden') {
        return <ModeliaForbiddenBlock description={forbiddenDescription} className={className} />;
    }
    if (state === 'error') {
        return (
            <ErrorState
                title={errorTitle}
                description={userMessageOf(error)}
                onRetry={onRetry}
                className={className}
            />
        );
    }
    return <EmptyState title={emptyTitle} description={emptyDescription} className={className} />;
}
